"use client";

import { IResource } from "@gettactic/api";
import { HookFormResourcePickerFieldInput } from "@gettactic/components/src/form/ResourcePickerFieldInput";
import { Armchair } from "lucide-react";
import React, { useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { ResourceFinderModal } from "@/features/map/components/modal/ResourceFinderModal";

interface SeatFieldInputProps {
  form: UseFormReturn<FieldValues>;
  name: string;
  label: string;
  isRequired?: boolean;
  description?: string;
}

/**
 * Seat form field. Owns the app's resource-finder modal (pinned to the request's office)
 * and renders the shared {@link HookFormResourcePickerFieldInput}. The stored value is the
 * picked resource id.
 */
export const SeatFieldInput: React.FC<SeatFieldInputProps> = ({
  form,
  name,
  label,
  isRequired = false,
  description
}) => {
  const officeId = form.watch("office_id") as string | undefined;
  const [isFinderOpen, setIsFinderOpen] = useState(false);
  const [pickedName, setPickedName] = useState<string | null>(null);

  const handleSelect = (resource: IResource) => {
    form.setValue(name, resource.id, {
      shouldValidate: true,
      shouldDirty: true
    });
    setPickedName(resource.label || resource.name);
    setIsFinderOpen(false);
  };

  return (
    <>
      <HookFormResourcePickerFieldInput
        form={form}
        name={name}
        label={label}
        isRequired={isRequired}
        description={description}
        icon={<Armchair className="h-4 w-4" />}
        placeholder={
          officeId
            ? "Pick a seat from the floorplan"
            : "Select a location first"
        }
        disabled={!officeId}
        clearLabel="Remove seat"
        onOpen={() => setIsFinderOpen(true)}
        getSelectedLabel={(value) =>
          value ? (pickedName ?? "Selected seat") : null
        }
        clearValue=""
      />

      <ResourceFinderModal
        isOpen={isFinderOpen}
        onClose={() => setIsFinderOpen(false)}
        initialOfficeId={officeId}
        lockOffice
        onSelect={handleSelect}
      />
    </>
  );
};
